import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";

type ProfileStatus = "none" | "generating" | "ready" | "failed";

interface ProfileState {
  status: ProfileStatus;
  image_url: string | null;
  face_count: number;
  regenerate_cost: number;
  error: string | null;
}

// No.4 の後: 顔写真から本人のビジネスプロフィール画像を生成する(必須ステップ)。
//   初回はフロー内で無料。再生成は 50cr(残高不足は案内)。
export function ProfileImagePage() {
  const { caseRow, loading } = useCurrentCase();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ProfileStatus | "loading">("loading");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [faceCount, setFaceCount] = useState(0);
  const [regenCost, setRegenCost] = useState(50);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const startedRef = useRef(false);

  const caseId = caseRow?.id;

  const fetchState = useCallback(async (id: string): Promise<ProfileState | null> => {
    try {
      const res = await api.get<ProfileState>(`/api/cases/${id}/profile-image`);
      setStatus(res.status);
      setImageUrl(res.image_url);
      setFaceCount(res.face_count);
      setRegenCost(res.regenerate_cost);
      return res;
    } catch {
      return null;
    }
  }, []);

  const startGenerate = useCallback(async (id: string) => {
    setError(null);
    setBusy(true);
    try {
      await api.post(`/api/cases/${id}/profile-image`, {});
      setStatus("generating");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "生成の開始に失敗しました");
    } finally {
      setBusy(false);
    }
  }, []);

  // 初回ロード: 状態取得 → 未生成かつ顔写真ありなら自動で(無料)生成を開始する。
  useEffect(() => {
    if (!caseId) return;
    let active = true;
    void (async () => {
      const res = await fetchState(caseId);
      if (!active || !res) return;
      if (res.status === "none" && res.face_count > 0 && !startedRef.current) {
        startedRef.current = true;
        await startGenerate(caseId);
      }
    })();
    return () => {
      active = false;
    };
  }, [caseId, fetchState, startGenerate]);

  // 生成中はポーリング。
  useEffect(() => {
    if (!caseId || status !== "generating") return;
    const iv = window.setInterval(() => {
      void fetchState(caseId);
    }, 2500);
    return () => window.clearInterval(iv);
  }, [caseId, status, fetchState]);

  if (loading || status === "loading") return <div className="page-center">読み込み中...</div>;
  if (!caseRow) return <div className="page-center">案件が見つかりません。</div>;

  const ready = status === "ready" && imageUrl;

  return (
    <section className="wizard">
      <div className="wizard-head">
        <span className="step-tag">AIプロフィール画像</span>
        <h1>AIプロフィール画像</h1>
        <p className="muted">
          アップロードいただいた顔写真をもとに、本人の顔立ち・髪型・年齢を保ったまま、
          ネイビースーツの自然なビジネスポートレートをAIが生成します。サイトにはこの画像を使用します。
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {faceCount === 0 ? (
        <div className="card">
          <p className="muted">
            プロフィール画像の生成には本人の顔写真が必要です。写真アップロードから顔写真を追加してください。
          </p>
          <Link to="/uploads" className="btn btn-ghost">
            ← 写真アップロードへ
          </Link>
        </div>
      ) : (
        <div className="card profile-image-card">
          <div className="profile-image-preview">
            {ready ? (
              <img src={imageUrl!} alt="AIプロフィール画像" />
            ) : status === "generating" ? (
              <div className="profile-image-placeholder">
                <div className="generating-spinner" />
                <p className="muted">AIがプロフィール画像を生成しています...</p>
              </div>
            ) : (
              <div className="profile-image-placeholder">
                <p className="muted">プロフィール画像を生成します。</p>
                <button
                  className="btn btn-primary"
                  onClick={() => caseId && startGenerate(caseId)}
                  disabled={busy}
                >
                  {busy ? "開始中..." : "生成する"}
                </button>
              </div>
            )}
          </div>

          {ready && (
            <div className="profile-image-actions">
              <p className="muted small">
                仕上がりがイメージと違う場合は、再生成できます（1回 {regenCost}cr）。
              </p>
              <button
                className="btn btn-ghost"
                onClick={() => caseId && startGenerate(caseId)}
                disabled={busy}
              >
                {busy ? "処理中..." : `再生成する（${regenCost}cr）`}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="wizard-actions wizard-actions-end">
        <button
          className="btn btn-primary btn-lg"
          onClick={() => navigate("/options")}
          disabled={!ready}
          title={!ready ? "プロフィール画像の生成が完了すると次へ進めます" : undefined}
        >
          AIオプションへ進む →
        </button>
      </div>
      {!ready && faceCount > 0 && (
        <p className="muted small center">
          プロフィール画像の生成が完了すると次へ進めます。
        </p>
      )}
    </section>
  );
}
